import { useLocation, useNavigate, Routes, Route } from 'react-router-dom'
import React, { useState, Suspense } from 'react'
import { Button } from './components/ui/button.jsx'
import { LessonCard } from './components/LessonCard.jsx'
import { Section } from './components/Section.jsx'
import { Curriculum } from './Curriculum.jsx'
import { SearchModal } from './components/SearchModal.jsx'
import { BookOpen, Calendar, Target, FileText, Search, Copy, Menu, X } from 'lucide-react'
import {
    parseLessonText,
    parseUnitNum,
    subjectKey,
    parseGradeId,
    buildElaData,
    buildSsData,
    weekdayIndexToday,
    resolveLessonWithOffset,
    resolveSsWithOffset,
} from './lib/planning.js'
import { getProgress, setProgress, onProgressChange, whenRemoteSettled, getLocalProgress } from './lib/storage.js'
const Standards = React.lazy(() => import('./Standards.jsx').then(m => ({ default: m.Standards })))

export function SpecialEdApp() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const currentWeek = "Week of December 9-13, 2024"
    const activePage = location.pathname.startsWith('/curriculum')
        ? 'curriculum'
        : location.pathname.startsWith('/standards')
        ? 'standards'
        : 'dashboard'

    // Subjects to show on the dashboard. If saved progress exists, we'll honor that; otherwise we seed defaults.
    const dashboardSubjects = React.useMemo(() => {
        // Try to reconstruct subjects from saved progress (so Curriculum and Dashboard stay in sync)
        let saved = {}
        try { saved = JSON.parse(localStorage.getItem('lp.progress') || '{}') } catch {}
        const savedEntries = Object.values(saved || {})
        const fromSaved = savedEntries
            .filter(v => /Skyline\s*ELA|Social\s*Science/i.test(String(v?.subject || ''))) // limit to ELA + SS
            .map(v => ({
                subject: v.subject,
                color: /Social\s*Science/i.test(v.subject) ? 'orange' : 'blue',
                type: /Social\s*Science/i.test(v.subject) ? 'ss' : 'ela',
                gradeId: v.gradeId || (/Social\s*Science/i.test(v.subject) ? 'gradek' : 'grade2'),
            }))
        if (fromSaved.length) return fromSaved
        // Fallback defaults
        return [
            { subject: '2nd Grade Skyline ELA', color: 'blue', type: 'ela', gradeId: 'grade2' },
            { subject: 'K Social Science', color: 'orange', type: 'ss', gradeId: 'gradek' },
        ]
    }, [])

    // Weekly schedule rows derive from dashboardSubjects colors
    const weeklySubjects = React.useMemo(() => {
        const out = {}
        for (const s of dashboardSubjects) {
            out[s.subject] = { color: s.color }
        }
        return out
    }, [dashboardSubjects])

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

    // Keyboard shortcut to open search (Cmd+K or Ctrl+K)
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setIsSearchOpen(true)
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <header className="bg-white border-b shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {/* Mobile menu button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="lg:hidden"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </Button>
                            <BookOpen className="h-8 w-8 text-blue-600" />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">CPS Lesson Planning Assistant</h1>
                                <p className="text-sm text-gray-500">Special Education Resources</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className="relative hidden md:flex items-center gap-2 pl-10 pr-4 py-2 border border-gray-300 rounded-md w-80 text-left text-gray-500 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <span className="flex-1">Search lessons, units, standards...</span>
                                <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-300 rounded">
                                    <span className="text-xs">⌘</span>K
                                </kbd>
                            </button>
                            {/* Header no longer shows page buttons; use sidebar/mobile nav */}
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex-1 flex min-h-0">
                {/* Sidebar Navigation - Hidden on mobile, shown on large screens */}
                <nav className="hidden lg:block w-64 bg-white border-r">
                    <div className="p-4">
                        <div className="space-y-2">
                            <Button
                                className="w-full justify-start"
                                variant={activePage === 'dashboard' ? 'default' : 'ghost'}
                                onClick={() => navigate('/')}
                            >
                                <Calendar className="h-4 w-4 mr-3" />
                                Dashboard
                            </Button>
                            <Button
                                variant={activePage === 'curriculum' ? 'default' : 'ghost'}
                                className="w-full justify-start"
                                onClick={() => navigate('/curriculum')}
                            >
                                <BookOpen className="h-4 w-4 mr-3" />
                                Curriculum
                            </Button>
                            <Button variant={activePage === 'standards' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => navigate('/standards')}>
                                <Target className="h-4 w-4 mr-3" />
                                Standards
                            </Button>
                            <Button variant="ghost" className="w-full justify-start">
                                <FileText className="h-4 w-4 mr-3" />
                                Resources
                            </Button>
                        </div>
                    </div>
                </nav>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
                        <nav className="fixed left-0 top-0 h-full w-64 bg-white border-r shadow-lg">
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold">Menu</h2>
                                    <Button variant="ghost" size="sm" onClick={() => setIsMobileMenuOpen(false)}>
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    <Button
                                        className="w-full justify-start"
                                        variant={activePage === 'dashboard' ? 'default' : 'ghost'}
                                        onClick={() => {
                                            navigate('/');
                                            setIsMobileMenuOpen(false);
                                        }}
                                    >
                                        <Calendar className="h-4 w-4 mr-3" />
                                        Dashboard
                                    </Button>
                                    <Button
                                        variant={activePage === 'curriculum' ? 'default' : 'ghost'}
                                        className="w-full justify-start text-gray-700"
                                        onClick={() => {
                                            navigate('/curriculum');
                                            setIsMobileMenuOpen(false);
                                        }}
                                    >
                                        <BookOpen className="h-4 w-4 mr-3" />
                                        Curriculum
                                    </Button>
                                    <Button variant={activePage === 'standards' ? 'default' : 'ghost'} className="w-full justify-start text-gray-700" onClick={() => { navigate('/standards'); setIsMobileMenuOpen(false); }}>
                                        <Target className="h-4 w-4 mr-3" />
                                        Standards
                                    </Button>
                                    <Button variant="ghost" className="w-full justify-start text-gray-700">
                                        <FileText className="h-4 w-4 mr-3" />
                                        Resources
                                    </Button>
                                </div>
                                {/* Mobile search */}
                                <div className="mt-4 sm:hidden">
                                    <button
                                        onClick={() => {
                                            setIsSearchOpen(true)
                                            setIsMobileMenuOpen(false)
                                        }}
                                        className="relative flex items-center gap-2 pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full text-left text-gray-500 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                    >
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        Search...
                                    </button>
                                </div>
                            </div>
                        </nav>
                    </div>
                )}

                {/* Main Content */}
                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <DashboardPage
                                    currentWeek={currentWeek}
                                    subjects={dashboardSubjects}
                                    weeklySubjects={weeklySubjects}
                                    days={days}
                                    onBrowseCurriculum={() => navigate('/curriculum')}
                                    navigate={navigate}
                                    setIsSearchOpen={setIsSearchOpen}
                                />
                            }
                        />
                        <Route path="/curriculum/*" element={<Curriculum />} />
                        <Route path="/standards" element={<Suspense fallback={<div>Loading standards…</div>}><Standards /></Suspense>} />
                    </Routes>
                </main>
            </div>
            
            {/* Search Modal */}
            <SearchModal 
                isOpen={isSearchOpen} 
                onClose={() => setIsSearchOpen(false)}
                onNavigate={(path) => navigate(path)}
            />
        </div>
    )
}

function DashboardPage({ currentWeek, subjects, weeklySubjects, days, onBrowseCurriculum, navigate, setIsSearchOpen }) {
    // Build ELA lesson totals by grade/unit from data for accurate counts
    // Extract a likely unit subtitle from Skyline titles
    const elaData = React.useMemo(() => buildElaData(), [])

    // Social Science totals (we only have Kindergarten data now)
    const ssData = React.useMemo(() => buildSsData(), [])

    const [progressByKey, setProgressByKey] = React.useState({})
    const [progressReady, setProgressReady] = React.useState(false)
    const isApplyingRemote = React.useRef(false)

    const buildFromSaved = React.useCallback((saved) => {
        const init = {}
        for (const c of subjects) {
            const key = subjectKey(c)
            const unitNum = 1
            const base = { current: 1, total: 1 }
            const gradeId = parseGradeId(c.subject)
            let dataTotal = base.total
            if (/Skyline\s*ELA/i.test(c.subject) && gradeId) {
                dataTotal = elaData.totals[`${gradeId}/unit${unitNum}`] || base.total
            } else if (/Social\s*Science/i.test(c.subject) && gradeId) {
                dataTotal = ssData.totalsNumeric[`${gradeId}/unit${unitNum}`] || base.total
            }
            const prev = saved?.[key]
            const isSS = /Social\s*Science/i.test(c.subject)
            const seqNow = isSS && gradeId ? (ssData.seq[`${gradeId}/unit${unitNum}`] || []) : []
            const hasL0 = !!seqNow.find(it => it.id === 'lesson0')
            const hasLi = !!seqNow.find(it => it.id === 'li')
            const hasIntro = isSS && (hasL0 || hasLi)
            const hasWrap = isSS && !!seqNow.find(it => it.id === 'lw')
            init[key] = prev ? {
                currentUnitNum: prev.currentUnitNum || unitNum,
                currentLesson: Math.min(prev.currentLesson || base.current, dataTotal),
                total: dataTotal,
                phase: prev.phase || (isSS ? (hasIntro ? 'intro' : 'number') : 'number'),
                introStep: prev.introStep != null ? prev.introStep : (isSS ? (hasL0 ? 0 : (hasLi ? 1 : null)) : null)
            } : {
                currentUnitNum: unitNum,
                currentLesson: Math.min(base.current, dataTotal),
                total: dataTotal,
                phase: isSS ? (hasIntro ? 'intro' : 'number') : 'number',
                introStep: isSS ? (hasL0 ? 0 : (hasLi ? 1 : null)) : null
            }
            init[key].gradeId = gradeId
            init[key].subject = c.subject
            if (isSS) {
                init[key].hasWrap = hasWrap; init[key].hasIntro = hasIntro; init[key].hasL0 = hasL0; init[key].hasLi = hasLi
            }
        }
        return init
    }, [subjects, elaData, ssData])

    // Hydrate from local (if present); otherwise wait for remote reconciliation before showing anything
    React.useEffect(() => {
        const localNow = getLocalProgress() || {}
        const hasLocal = Object.keys(localNow || {}).length > 0
        if (hasLocal) {
            isApplyingRemote.current = true
            setProgressByKey(buildFromSaved(localNow))
            isApplyingRemote.current = false
            setProgressReady(true)
            // Kick remote reconcile in background
            void getProgress()
        } else {
            // No local yet: prefer remote snapshot, then fall back to defaults if remote empty
            ;(async () => {
                isApplyingRemote.current = true
                void getProgress() // starts remote reconcile
                await whenRemoteSettled()
                const after = getLocalProgress() || {}
                if (Object.keys(after || {}).length > 0) {
                    setProgressByKey(buildFromSaved(after))
                } else {
                    // Still empty: seed defaults but do not write to remote until user interacts
                    setProgressByKey(buildFromSaved({}))
                }
                isApplyingRemote.current = false
                setProgressReady(true)
            })()
        }

        // Subscribe to storage changes (remote sync will update local and fire event)
        const off = onProgressChange((latest) => {
            isApplyingRemote.current = true
            setProgressByKey(buildFromSaved(latest))
            isApplyingRemote.current = false
        })
        return () => { if (off) off() }
    }, [buildFromSaved])

    // Persist only when changes come from user interactions (not from remote hydration) and after ready
    React.useEffect(() => {
        if (!progressReady) return
        if (!Object.keys(progressByKey || {}).length) return
        if (isApplyingRemote.current) return
        setProgress(progressByKey)
    }, [progressByKey, progressReady])

    const goPrevLesson = (key) => {
        setProgressByKey(prev => {
            const v = prev[key]
            if (!v) return prev
            // If at start of step 0, go back a lesson if possible
            const isSS = /Social\s*Science/i.test(v.subject || '')
            if (isSS) {
                if (v.phase === 'wrap') {
                    // Go back to final numeric lesson
                    return { ...prev, [key]: { ...v, phase: 'number', currentLesson: v.total } }
                }
                if (v.phase === 'number') {
                    if (v.currentLesson > 1) {
                        return { ...prev, [key]: { ...v, currentLesson: v.currentLesson - 1 } }
                    }
                    if (v.hasIntro) {
                        // Move to the last intro step available (prefer introduction if present)
                        const backIntroStep = v.hasLi ? 1 : (v.hasL0 ? 0 : null)
                        return { ...prev, [key]: { ...v, phase: 'intro', introStep: backIntroStep } }
                    }
                }
                if (v.phase === 'intro') {
                    // Move within intro sequence: 1 -> 0 if lesson0 exists; else go to previous unit
                    if (v.introStep === 1 && v.hasL0) {
                        return { ...prev, [key]: { ...v, introStep: 0 } }
                    }
                }
            } else {
                if (v.currentLesson > 1) {
                    return { ...prev, [key]: { ...v, currentLesson: v.currentLesson - 1 } }
                }
            }
            // Optionally go back a unit if at lesson 1
            if (v.currentUnitNum > 1 && v.gradeId) {
                const prevUnit = v.currentUnitNum - 1
                const isELA = /Skyline\s*ELA/i.test(v.subject || '')
                if (isELA) {
                    const totalPrev = elaData.totals[`${v.gradeId}/unit${prevUnit}`] || v.total
                    return { ...prev, [key]: { ...v, currentUnitNum: prevUnit, currentLesson: totalPrev, total: totalPrev } }
                } else {
                    const totalPrev = ssData.totalsNumeric[`${v.gradeId}/unit${prevUnit}`] || v.total
                    const prevSeq = (ssData.seq[`${v.gradeId}/unit${prevUnit}`] || [])
                    const hasL0Prev = !!prevSeq.find(it => it.id === 'lesson0')
                    const hasLiPrev = !!prevSeq.find(it => it.id === 'li')
                    const hasIntroPrev = hasL0Prev || hasLiPrev
                    const hasWrapPrev = !!prevSeq.find(it => it.id === 'lw')
                    const phasePrev = hasWrapPrev ? 'wrap' : (totalPrev ? 'number' : (hasIntroPrev ? 'intro' : 'number'))
                    const introStepPrev = hasLiPrev ? 1 : (hasL0Prev ? 0 : null)
                    return { ...prev, [key]: { ...v, currentUnitNum: prevUnit, currentLesson: totalPrev, total: totalPrev, phase: phasePrev, hasIntro: hasIntroPrev, hasWrap: hasWrapPrev, hasL0: hasL0Prev, hasLi: hasLiPrev, introStep: introStepPrev } }
                }
            }
            return prev
        })
    }
    const goNextLesson = (key, subject) => {
        setProgressByKey(prev => {
            const v = prev[key]
            if (!v) return prev
            // Move to next lesson or next unit
            const isSS = /Social\s*Science/i.test(subject)
            if (isSS) {
                if (v.phase === 'intro') {
                    // Two-step intro: lesson0 (0) -> introduction (1) -> number phase
                    if (v.introStep === 0 && v.hasLi) {
                        return { ...prev, [key]: { ...v, introStep: 1 } }
                    }
                    return { ...prev, [key]: { ...v, phase: 'number', currentLesson: Math.max(1, v.currentLesson || 1) } }
                }
                if (v.phase === 'number') {
                    if (v.currentLesson < v.total) {
                        return { ...prev, [key]: { ...v, currentLesson: v.currentLesson + 1 } }
                    }
                    if (v.hasWrap) {
                        return { ...prev, [key]: { ...v, phase: 'wrap' } }
                    }
                }
                // phase wrap or no wrap → next unit
                const nextUnit = v.currentUnitNum + 1
                if (v.gradeId) {
                    const nextTotal = ssData.totalsNumeric[`${v.gradeId}/unit${nextUnit}`]
                    if (nextTotal) {
                        const nextSeq = (ssData.seq[`${v.gradeId}/unit${nextUnit}`] || [])
                        const hasL0Next = !!nextSeq.find(it => it.id === 'lesson0')
                        const hasLiNext = !!nextSeq.find(it => it.id === 'li')
                        const hasIntroNext = hasL0Next || hasLiNext
                        const hasWrapNext = !!nextSeq.find(it => it.id === 'lw')
                        const introStepNext = hasL0Next ? 0 : (hasLiNext ? 1 : null)
                        return { ...prev, [key]: { ...v, currentUnitNum: nextUnit, currentLesson: 1, total: nextTotal, phase: hasIntroNext ? 'intro' : 'number', hasIntro: hasIntroNext, hasWrap: hasWrapNext, hasL0: hasL0Next, hasLi: hasLiNext, introStep: introStepNext } }
                    }
                }
                return prev
            }
            // ELA
            if (v.currentLesson < v.total) {
                return { ...prev, [key]: { ...v, currentLesson: v.currentLesson + 1 } }
            }
            const nextUnit = v.currentUnitNum + 1
            if (v.gradeId) {
                const nextTotal = elaData.totals[`${v.gradeId}/unit${nextUnit}`]
                if (nextTotal) {
                    return { ...prev, [key]: { ...v, currentUnitNum: nextUnit, currentLesson: 1, total: nextTotal } }
                }
            }
            return prev
        })
    }
    const openLesson = (c) => {
        const key = subjectKey(c)
        const v = progressByKey[key]
        const currentLesson = v?.currentLesson || parseLessonText(c.lesson).current
        const unitNum = v?.currentUnitNum || parseUnitNum(c.unit)
        const gradeId = v?.gradeId
        if (/Skyline\s*ELA/i.test(c.subject) && gradeId && unitNum) {
            navigate(`/curriculum/ela/${gradeId}/unit${unitNum}/lesson${currentLesson}`)
        } else if (/Social\s*Science/i.test(c.subject) && gradeId && unitNum) {
            // Decide id based on phase
            const seq = ssData.seq[`${gradeId}/unit${unitNum}`] || []
            let id = `lesson${currentLesson}`
            if (v?.phase === 'intro') {
                // Use introStep to select lesson0 (0) then introduction (1)
                if (v?.introStep === 0) {
                    id = seq.find(it => it.id === 'lesson0')?.id || seq.find(it => it.id === 'li')?.id || id
                } else if (v?.introStep === 1) {
                    id = seq.find(it => it.id === 'li')?.id || seq.find(it => it.id === 'lesson0')?.id || id
                } else {
                    id = seq.find(it => it.id === 'lesson0')?.id || seq.find(it => it.id === 'li')?.id || id
                }
            } else if (v?.phase === 'wrap') {
                id = seq.find(it => it.id === 'lw')?.id || id
            }
            navigate(`/curriculum/social-science/${gradeId}/unit${unitNum}/${id}`)
        } else {
            navigate('/curriculum')
        }
    }

    const onWeeklyViewDetails = (subject, dayIndex) => {
        const key = (subject || '').toLowerCase()
        const v = progressByKey[key]
        const todayIdx = weekdayIndexToday(days)
        const offset = dayIndex - todayIdx
        if (/Skyline\s*ELA/i.test(subject) && v?.gradeId) {
            const { unit, lesson } = resolveLessonWithOffset(elaData.totals, v.gradeId, v.currentUnitNum, v.currentLesson, offset)
            navigate(`/curriculum/ela/${v.gradeId}/unit${unit}/lesson${lesson}`)
        } else if (/Social\s*Science/i.test(subject) && v?.gradeId) {
            const { unit, id } = resolveSsWithOffset(ssData, v.gradeId, v.currentUnitNum, v.currentLesson, v.phase, offset, v.introStep)
            navigate(`/curriculum/social-science/${v.gradeId}/unit${unit}/${id}`)
        } else {
            navigate('/curriculum')
        }
    }
    return (
        <div className="space-y-6">
            {/* Welcome Section */}
            <div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Welcome back!</h2>
                <p className="text-gray-600">Here's your lesson planning overview for {currentWeek}</p>
            </div>

            {/* Current lessons - align with Curriculum page header */}
            <Section>
                <div className="flex items-center mb-4">
                    <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                    <h3 className="text-lg font-semibold">Current lessons</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {subjects.map((c, index) => {
                        const key = subjectKey(c)
                        const v = progressByKey[key] || { currentLesson: 1, total: 1 }
                        const isSS = /Social\s*Science/i.test(c.subject)
                        let headline
                        if (isSS && v?.gradeId) {
                            const seqKey = `${v.gradeId}/unit${v.currentUnitNum}`
                            const seq = ssData.seq[seqKey] || []
                            let idx = -1
                            if (v.phase === 'intro') {
                                if (v.introStep === 0) idx = seq.findIndex(it => it.id === 'lesson0')
                                else if (v.introStep === 1) idx = seq.findIndex(it => it.id === 'li')
                                if (idx === -1) idx = seq.findIndex(it => it.id === 'lesson0')
                                if (idx === -1) idx = seq.findIndex(it => it.id === 'li')
                            } else if (v.phase === 'wrap') {
                                idx = seq.findIndex(it => it.id === 'lw')
                            } else {
                                const numIdx = ssData.numIndex[seqKey] || {}
                                idx = numIdx[v.currentLesson] != null ? numIdx[v.currentLesson] : -1
                            }
                            if (idx === -1) idx = 0
                            const displayCount = idx + 1
                            const totalSeq = seq.length || v.total
                            const namePart = v.phase === 'intro' ? (v.introStep === 0 ? 'Lesson 0' : 'Introduction') : (v.phase === 'wrap' ? 'Wrap-Up' : `Lesson ${v.currentLesson}`)
                            headline = `Unit ${v.currentUnitNum} • ${namePart} • Lesson ${displayCount} of ${totalSeq}`
                        } else {
                            headline = `Unit ${v.currentUnitNum} • Lesson ${v.currentLesson} of ${v.total}`
                        }
                        return (
                            <LessonCard
                                key={index}
                                color={c.color}
                                subjectLabel={c.subject}
                                headline={headline}
                                onView={() => openLesson(c)}
                                onPrev={() => goPrevLesson(key)}
                                onNext={() => goNextLesson(key, c.subject)}
                            />
                        )
                    })}
                </div>
            </Section>

            {/* Weekly Lessons Grid */}
            <Section>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                    <div className="flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-green-600" />
                        <h3 className="text-lg font-semibold">This Week's Lessons</h3>
                    </div>
                    <Button variant="outline" size="sm">
                        <Copy className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Copy Week Plan</span>
                        <span className="sm:hidden">Copy</span>
                    </Button>
                </div>
                <p className="text-sm text-gray-500 mb-4">{currentWeek}</p>

                {/* Horizontally scrollable schedule (all screen sizes) */}
                <div className="overflow-x-auto block">
                    <table className="w-full border-collapse min-w-[680px]">
                        <thead>
                            <tr>
                                <th className="text-left p-3 border-b font-medium text-gray-700 w-48">Subject/Grade</th>
                                {days.map((day) => (
                                    <th key={day} className="text-left p-3 border-b font-medium text-gray-700 min-w-[200px]">
                                        {day}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(weeklySubjects).map(([subject, data]) => (
                                <tr key={subject} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-medium text-sm">
                                        <div className={`inline-block w-3 h-3 rounded-full bg-${data.color}-500 mr-2`}></div>
                                        {subject}
                                    </td>
                                    {days.map((day, dayIndex) => (
                                        <td key={day} className="p-3">
                                            <div className="space-y-2">
                                                {(() => {
                                                    const key = subject.toLowerCase()
                                                    const v = Object.values(progressByKey || {}).find(x => (x?.subject || '').toLowerCase() === key)
                                                    const todayIdx = weekdayIndexToday(days)
                                                    const offset = dayIndex - todayIdx
                                                    if (/Skyline\s*ELA/i.test(subject) && v?.gradeId) {
                                                        const { unit, lesson } = resolveLessonWithOffset(elaData.totals, v.gradeId, v.currentUnitNum, v.currentLesson, offset)
                                                        const rawTitle = elaData.unitTitles[`${v.gradeId}/unit${unit}`] || `Unit ${unit}`
                                                        const title = rawTitle.replace(/^Unit\s*\d+\s*:\s*/i, '')
                                                        return (
                                                            <>
                                                                <h4 className="font-medium text-sm">{title}</h4>
                                                                <p className="text-xs text-gray-500">{`Unit ${unit} • Lesson ${lesson}`}</p>
                                                            </>
                                                        )
                                                    } else if (/Social\s*Science/i.test(subject) && v?.gradeId) {
                                                        const { unit, id } = resolveSsWithOffset(ssData, v.gradeId, v.currentUnitNum, v.currentLesson, v.phase, offset, v.introStep)
                                                        const rawTitle = ssData.titles[`${v.gradeId}/unit${unit}/${id}`] || id.toUpperCase()
                                                        const title = rawTitle.replace(/^Lesson\s*\d+\s*:\s*/i, '')
                                                        const lessonLabel = id.startsWith('lesson') ? `Lesson ${id.replace(/\D/g, '')}` : (id === 'li' ? 'Intro' : id === 'lw' ? 'Wrap-Up' : id.toUpperCase())
                                                        return (
                                                            <>
                                                                <h4 className="font-medium text-sm">{title}</h4>
                                                                <p className="text-xs text-gray-500">{`Unit ${unit} • ${lessonLabel}`}</p>
                                                            </>
                                                        )
                                                    } else {
                                                        return <h4 className="font-medium text-sm">—</h4>
                                                    }
                                                })()}
                                                <Button size="sm" className={`text-xs h-6 px-2 bg-${data.color}-500 hover:bg-${data.color}-600 text-white`}
                                                    onClick={() => onWeeklyViewDetails(subject, dayIndex)}
                                                >
                                                    View lesson
                                                </Button>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Removed mobile condensed list to keep a single horizontal schedule */}
            </Section>

            {/* Quick Actions - Simplified */}
            <Section title="Quick Actions">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Button className="justify-start" variant="outline" onClick={onBrowseCurriculum}>
                        <BookOpen className="h-4 w-4 mr-2" />
                        Browse Curriculum
                    </Button>
                    <Button className="justify-start" variant="outline" onClick={() => setIsSearchOpen(true)}>
                        <Search className="h-4 w-4 mr-2" />
                        Search All
                    </Button>
                    <Button className="justify-start" variant="outline">
                        <FileText className="h-4 w-4 mr-2" />
                        View Resources
                    </Button>
                    <Button className="justify-start" variant="outline">
                        <Copy className="h-4 w-4 mr-2" />
                        Export Templates
                    </Button>
                </div>
            </Section>
        </div>
    )
}

// removed duplicate helpers; now imported from lib/planning.js
